<template>
  <div class="quotation-detail" v-loading="store.detailLoading">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="header-left">
        <el-button @click="goBack" :icon="ArrowLeft" circle />
        <h2>{{ q?.quotation_number || '견적서 상세' }}</h2>
        <el-tag v-if="q" :type="q.quotation_type === 'PC' ? 'warning' : 'primary'" size="large">
          {{ q.quotation_type_display }}
        </el-tag>
        <el-tag v-if="q" :type="getStatusType(q.status)" size="large">
          {{ q.status_display }}
        </el-tag>
        <el-icon v-if="q?.is_locked" color="#E6A23C" :size="20"><Lock /></el-icon>
      </div>
      <div class="header-actions">
        <el-button @click="showCompareDialog = true">
          <el-icon><DataAnalysis /></el-icon>
          과거 견적 비교
        </el-button>
        <el-button v-if="canViewCosts" @click="openSimulation">
          <el-icon><TrendCharts /></el-icon>
          원가 시뮬레이션
        </el-button>
        <el-button v-if="q && !q.is_locked" type="primary" @click="handleRecalculate">
          <el-icon><Refresh /></el-icon>
          원가 재계산
        </el-button>
        <el-button
          v-if="q && !q.is_locked"
          type="warning"
          @click="handleConfirm"
        >
          확정
        </el-button>
        <el-button
          v-if="q && q.is_locked"
          @click="handleUnconfirm"
        >
          확정 취소
        </el-button>
        <el-button
          v-if="q && q.quotation_type === 'PC' && !q.is_locked"
          type="success"
          @click="handleConvert"
        >
          견적서로 전환
        </el-button>
        <el-button v-if="q && !q.is_locked" @click="handleCreateRevision">
          <el-icon><DocumentCopy /></el-icon>
          리비전 생성
        </el-button>
        <el-button
          v-if="q && q.status === 'CONFIRMED' && (!q.main_requests_info || q.main_requests_info.length === 0)"
          type="success"
          @click="handleCreateMainRequest"
        >
          <el-icon><Plus /></el-icon>
          메인오더 생성
        </el-button>
        <el-button
          v-if="q && q.main_requests_info && q.main_requests_info.length > 0"
          type="info"
          plain
          @click="goToMainRequest(q.main_requests_info[0].id)"
        >
          메인오더 보기 ({{ q.main_requests_info[0].request_number }})
        </el-button>
        <el-button @click="helpVisible = true">도움말</el-button>
        <HelpPanel v-model="helpVisible" :content-path="'screens/quotation-detail'" />
        <el-button @click="handlePrint">
          <el-icon><Printer /></el-icon>
          인쇄
        </el-button>
      </div>
    </div>

    <!-- 요약 카드 -->
    <el-row :gutter="20" class="summary-row" v-if="q">
      <!-- 디자인 이미지 -->
      <el-col :span="3" v-if="q.design_image_url">
        <el-card shadow="hover" class="summary-card design-image-card">
          <el-image
            :src="q.design_image_url"
            fit="contain"
            style="width: 100%; height: 80px;"
            :preview-src-list="[q.design_image_url]"
          >
            <template #error>
              <div class="image-placeholder">이미지 없음</div>
            </template>
          </el-image>
        </el-card>
      </el-col>
      <el-col :span="colSpan">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">브랜드</div>
          <div class="summary-value">{{ q.brand_name }}</div>
        </el-card>
      </el-col>
      <el-col :span="colSpan">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">스타일번호</div>
          <div class="summary-value">{{ q.design_style_number }}</div>
        </el-card>
      </el-col>
      <el-col :span="colSpan">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">발주수량</div>
          <div class="summary-value">{{ q.order_quantity?.toLocaleString() }}</div>
        </el-card>
      </el-col>
      <el-col :span="colSpan" v-if="canViewCosts">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">견적가</div>
          <div class="summary-value cost-highlight">{{ formatCurrency(q.quotation_price) }}</div>
        </el-card>
      </el-col>
      <el-col :span="colSpan" v-if="canViewCosts">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">PCS단가</div>
          <div class="summary-value cost-highlight">{{ formatCurrency(q.effective_unit_price) }}</div>
        </el-card>
      </el-col>
      <el-col :span="colSpan" v-if="canViewCosts && q.negotiated_price">
        <el-card shadow="hover" class="summary-card">
          <div class="summary-label">합의가(NEGO)</div>
          <div class="summary-value nego-highlight">{{ formatCurrency(q.negotiated_price) }}</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 견적 히스토리 (같은 그룹 내 버전 타임라인) -->
    <div v-if="q?.group_history?.length > 1" class="history-timeline">
      <div class="history-label">견적 이력</div>
      <div class="history-steps">
        <div
          v-for="h in q.group_history"
          :key="h.id"
          class="history-step"
          :class="{
            'is-current': h.is_current,
            'is-confirmed': h.status === 'CONFIRMED',
            'is-superseded': h.status === 'SUPERSEDED',
          }"
          @click="h.is_current ? null : $router.push({ name: 'quotation-detail', params: { id: h.id } })"
        >
          <div class="step-type">{{ h.quotation_type === 'PC' ? '사전원가' : '견적서' }}</div>
          <div class="step-number">{{ h.quotation_number }}</div>
          <div class="step-version">v{{ h.version }}</div>
          <div class="step-status">{{ getStatusLabel(h.status) }}</div>
        </div>
      </div>
    </div>

    <!-- 4탭 구조 -->
    <el-tabs v-model="activeTab" class="detail-tabs">
      <!-- Tab 1: 기본정보 -->
      <el-tab-pane label="기본정보" name="info">
        <el-card>
          <div class="tab-header">
            <span>기본 정보</span>
            <el-button size="small" @click="showEditInfo = true">
              {{ q?.is_locked ? '합의가/메모 수정' : '수정' }}
            </el-button>
          </div>
          <el-descriptions :column="2" border v-if="q">
            <el-descriptions-item label="견적번호">{{ q.quotation_number }}</el-descriptions-item>
            <el-descriptions-item label="견적유형">{{ q.quotation_type_display }}</el-descriptions-item>
            <el-descriptions-item label="브랜드">{{ q.brand_name }}</el-descriptions-item>
            <el-descriptions-item label="스타일번호">{{ q.design_style_number }}</el-descriptions-item>
            <el-descriptions-item label="디자이너">{{ q.designer_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="생산공장">{{ q.manufacturer_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="발주수량">{{ q.order_quantity?.toLocaleString() }}</el-descriptions-item>
            <el-descriptions-item label="환율">{{ q.exchange_rate }}</el-descriptions-item>
            <el-descriptions-item label="견적요청일">{{ q.request_date }}</el-descriptions-item>
            <el-descriptions-item label="납기일">{{ q.delivery_date || '-' }}</el-descriptions-item>
            <el-descriptions-item label="샘플완성일">{{ q.sample_completion_date || '-' }}</el-descriptions-item>
            <el-descriptions-item v-if="canViewCosts" label="마진율">{{ q.margin_rate }}%</el-descriptions-item>
            <el-descriptions-item v-if="canViewCosts" label="합의가(NEGO)">
              <span v-if="q.negotiated_price" class="nego-highlight">{{ formatCurrency(q.negotiated_price) }}</span>
              <span v-else>-</span>
            </el-descriptions-item>
            <el-descriptions-item label="원본견적">{{ q.source_quotation_number || '-' }}</el-descriptions-item>
            <el-descriptions-item label="샘플요청">{{ q.sample_request_number || '-' }}</el-descriptions-item>
            <el-descriptions-item label="메모" :span="2">{{ q.memo || '-' }}</el-descriptions-item>
          </el-descriptions>

          <!-- 원가 요약 테이블 -->
          <template v-if="canViewCosts">
            <h4 style="margin-top: 20px;">원가 요약</h4>
            <el-table :data="costSummary" border stripe size="small" style="width: 500px;">
              <el-table-column prop="label" label="항목" width="200" />
              <el-table-column label="금액" align="right">
                <template #default="{ row }">
                  <span :class="{ 'cost-highlight': row.highlight && !row.nego, 'nego-highlight': row.nego }">
                    {{ formatCurrency(row.amount) }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </template>

          <!-- 사이즈 배분 -->
          <h4 style="margin-top: 20px;">
            사이즈 배분
            <el-tag v-if="!sizeDistributions.length" size="small" type="info" style="margin-left: 8px;">미설정</el-tag>
          </h4>
          <el-table v-if="sizeDistributions.length" :data="sizeDistributions" border stripe size="small" style="width: 500px;" show-summary :summary-method="getSizeDistSummary">
            <el-table-column prop="size" label="사이즈" width="100" />
            <el-table-column prop="ratio" label="비율" width="100" align="right" />
            <el-table-column prop="quantity" label="수량" width="120" align="right">
              <template #default="{ row }">{{ row.quantity?.toLocaleString() }}</template>
            </el-table-column>
          </el-table>
          <div style="margin-top: 8px;" v-if="!q?.is_locked">
            <el-button size="small" @click="showSizeDistDialog = true">
              {{ sizeDistributions.length ? '수정' : '설정' }}
            </el-button>
            <el-dropdown v-if="designMarkers.length" trigger="click" @command="handleImportFromMarker" style="margin-left: 8px;">
              <el-button size="small">마카에서 가져오기<el-icon class="el-icon--right"><ArrowDown /></el-icon></el-button>
              <template #dropdown>
                <el-dropdown-menu>
                  <el-dropdown-item
                    v-for="marker in designMarkers"
                    :key="marker.id"
                    :command="marker.id"
                  >
                    {{ marker.marker_name }} ({{ marker.marker_type_display }})
                  </el-dropdown-item>
                </el-dropdown-menu>
              </template>
            </el-dropdown>
          </div>
        </el-card>
      </el-tab-pane>

      <!-- Tab 2: 원부자재비 -->
      <el-tab-pane v-if="canViewCosts" label="원부자재비" name="materials">
        <el-card>
          <div class="tab-header">
            <span>원부자재 목록</span>
            <div v-if="!q?.is_locked" class="tab-header-actions">
              <el-button type="primary" size="small" @click="openMaterialDialog()">수동 추가</el-button>
              <el-dropdown trigger="click" @command="handleMaterialImport">
                <el-button size="small">가져오기<el-icon class="el-icon--right"><ArrowDown /></el-icon></el-button>
                <template #dropdown>
                  <el-dropdown-menu>
                    <el-dropdown-item command="yardage" :disabled="!q?.design">요척에서 가져오기</el-dropdown-item>
                    <el-dropdown-item command="sample">샘플에서 가져오기</el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </div>
          </div>

          <!-- 원자재 -->
          <h4>원자재 (RAW)</h4>
          <el-table :data="rawMaterials" border stripe size="small" show-summary :summary-method="getMaterialSummary" row-key="id">
            <el-table-column type="expand" width="30">
              <template #default="{ row }">
                <div v-if="row.is_size_dependent && row.size_usages?.length" class="size-usage-detail">
                  <span class="size-usage-label">사이즈별 소요량:</span>
                  <el-tag v-for="su in row.size_usages" :key="su.id" size="small" style="margin-right: 6px;">
                    {{ su.size }}: {{ su.usage_per_unit }}
                  </el-tag>
                </div>
                <div v-else class="size-usage-detail">
                  <span class="size-usage-label">단일 소요량 (사이즈 무관)</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="sequence" label="No" width="50" align="center" />
            <el-table-column prop="item_name" label="품명" width="150">
              <template #default="{ row }">
                {{ row.item_name }}
                <el-tag v-if="row.is_size_dependent" size="small" type="success" style="margin-left: 4px;">S</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="specification" label="규격" width="100" />
            <el-table-column prop="color" label="색상" width="80" />
            <el-table-column label="PCS소요량" width="100" align="right">
              <template #default="{ row }">
                <span :title="row.is_size_dependent ? '사이즈별 가중평균' : ''">
                  {{ row.usage_per_unit }}{{ row.is_size_dependent ? '*' : '' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="총소요량" width="100" align="right">
              <template #default="{ row }">{{ row.required_quantity }}</template>
            </el-table-column>
            <el-table-column label="단가" width="100" align="right">
              <template #default="{ row }">
                {{ row.currency !== 'KRW' ? `${row.unit_price_foreign} ${row.currency}` : formatCurrency(row.unit_price_krw) }}
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="금액" width="120" align="right">
              <template #default="{ row }">{{ formatCurrency(row.amount) }}</template>
            </el-table-column>
            <el-table-column label="CMT" width="60" align="center">
              <template #default="{ row }">
                <el-tag v-if="row.is_cmt" size="small" type="info">CMT</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="120" v-if="!q?.is_locked">
              <template #default="{ row }">
                <el-button size="small" @click.stop="openMaterialDialog(row)">수정</el-button>
                <el-button size="small" type="danger" @click.stop="handleDeleteMaterial(row)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 부자재 -->
          <h4 style="margin-top: 20px;">부자재 (ACC)</h4>
          <el-table :data="accMaterials" border stripe size="small" show-summary :summary-method="getMaterialSummary" row-key="id">
            <el-table-column type="expand" width="30">
              <template #default="{ row }">
                <div v-if="row.is_size_dependent && row.size_usages?.length" class="size-usage-detail">
                  <span class="size-usage-label">사이즈별 소요량:</span>
                  <el-tag v-for="su in row.size_usages" :key="su.id" size="small" style="margin-right: 6px;">
                    {{ su.size }}: {{ su.usage_per_unit }}
                  </el-tag>
                </div>
                <div v-else class="size-usage-detail">
                  <span class="size-usage-label">단일 소요량 (사이즈 무관)</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column prop="sequence" label="No" width="50" align="center" />
            <el-table-column prop="item_name" label="품명" width="150">
              <template #default="{ row }">
                {{ row.item_name }}
                <el-tag v-if="row.is_size_dependent" size="small" type="success" style="margin-left: 4px;">S</el-tag>
              </template>
            </el-table-column>
            <el-table-column prop="specification" label="규격" width="100" />
            <el-table-column label="PCS소요량" width="100" align="right">
              <template #default="{ row }">
                <span :title="row.is_size_dependent ? '사이즈별 가중평균' : ''">
                  {{ row.usage_per_unit }}{{ row.is_size_dependent ? '*' : '' }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="총소요량" width="100" align="right">
              <template #default="{ row }">{{ row.required_quantity }}</template>
            </el-table-column>
            <el-table-column label="단가" width="100" align="right">
              <template #default="{ row }">
                {{ row.currency !== 'KRW' ? `${row.unit_price_foreign} ${row.currency}` : formatCurrency(row.unit_price_krw) }}
              </template>
            </el-table-column>
            <el-table-column prop="amount" label="금액" width="120" align="right">
              <template #default="{ row }">{{ formatCurrency(row.amount) }}</template>
            </el-table-column>
            <el-table-column label="CMT" width="60" align="center">
              <template #default="{ row }">
                <el-tag v-if="row.is_cmt" size="small" type="info">CMT</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="" width="120" v-if="!q?.is_locked">
              <template #default="{ row }">
                <el-button size="small" @click.stop="openMaterialDialog(row)">수정</el-button>
                <el-button size="small" type="danger" @click.stop="handleDeleteMaterial(row)">삭제</el-button>
              </template>
            </el-table-column>
          </el-table>

          <!-- 자재비 소계 -->
          <el-row :gutter="20" style="margin-top: 20px;">
            <el-col :span="12">
              <el-descriptions :column="1" border size="small" title="자재비 소계">
                <el-descriptions-item label="자체 원자재비">{{ formatCurrency(q?.self_raw_material_cost) }}</el-descriptions-item>
                <el-descriptions-item label="브랜드 원자재비">{{ formatCurrency(q?.brand_raw_material_cost) }}</el-descriptions-item>
                <el-descriptions-item label="자체 부자재비">{{ formatCurrency(q?.self_accessory_cost) }}</el-descriptions-item>
                <el-descriptions-item label="브랜드 부자재비">{{ formatCurrency(q?.brand_accessory_cost) }}</el-descriptions-item>
                <el-descriptions-item label="자체부담 소계">
                  <span class="cost-highlight">{{ formatCurrency(q?.self_material_subtotal) }}</span>
                </el-descriptions-item>
              </el-descriptions>
            </el-col>
          </el-row>
        </el-card>
      </el-tab-pane>

      <!-- Tab 3: 임가공비 -->
      <el-tab-pane v-if="canViewCosts" label="임가공비" name="cmt">
        <el-card>
          <div class="tab-header">
            <span>임가공(CMT) 정보</span>
            <el-button v-if="!q?.is_locked" type="primary" size="small" @click="showCmtEdit = true">
              {{ q?.cmt_detail ? '수정' : '입력' }}
            </el-button>
          </div>
          <el-descriptions :column="2" border v-if="q?.cmt_detail">
            <el-descriptions-item label="생산공장">{{ q.cmt_detail.manufacturer_name || '-' }}</el-descriptions-item>
            <el-descriptions-item label="생산국가">{{ q.cmt_detail.country || '-' }}</el-descriptions-item>
            <el-descriptions-item label="외화 임가공비">{{ q.cmt_detail.cmt_price_foreign }} {{ q.cmt_detail.currency }}</el-descriptions-item>
            <el-descriptions-item label="환율">{{ q.cmt_detail.exchange_rate }}</el-descriptions-item>
            <el-descriptions-item label="원화 임가공비">
              <span class="cost-highlight">{{ formatCurrency(q.cmt_detail.cmt_price_krw) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="비고">{{ q.cmt_detail.remarks || '-' }}</el-descriptions-item>
          </el-descriptions>
          <el-empty v-else description="임가공 정보가 없습니다." />
        </el-card>
      </el-tab-pane>

      <!-- Tab 4: 경비 -->
      <el-tab-pane v-if="canViewCosts" label="경비" name="expense">
        <el-card>
          <div class="tab-header">
            <span>경비(물류/관세) 정보</span>
            <el-button v-if="!q?.is_locked" type="primary" size="small" @click="showExpenseEdit = true">
              {{ q?.expense_detail ? '수정' : '입력' }}
            </el-button>
          </div>
          <el-descriptions :column="2" border v-if="q?.expense_detail">
            <el-descriptions-item label="물류비율">{{ q.expense_detail.logistics_rate }}%</el-descriptions-item>
            <el-descriptions-item label="물류비">
              <span class="cost-highlight">{{ formatCurrency(q.expense_detail.logistics_amount) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="관세비율">{{ q.expense_detail.customs_rate }}%</el-descriptions-item>
            <el-descriptions-item label="관세">
              <span class="cost-highlight">{{ formatCurrency(q.expense_detail.customs_amount) }}</span>
            </el-descriptions-item>
            <el-descriptions-item label="비고" :span="2">{{ q.expense_detail.remarks || '-' }}</el-descriptions-item>
          </el-descriptions>
          <el-empty v-else description="경비 정보가 없습니다." />
        </el-card>
      </el-tab-pane>
    </el-tabs>

    <!-- 자재 편집 다이얼로그 -->
    <el-dialog v-model="showMaterialDialog" :title="editingMaterial ? '자재 수정' : '자재 추가'" width="600px">
      <el-form :model="materialForm" label-width="120px">
        <el-form-item label="자재유형" required>
          <el-select v-model="materialForm.material_type">
            <el-option label="원자재" value="RAW" />
            <el-option label="부자재" value="ACC" />
          </el-select>
        </el-form-item>
        <el-form-item label="품명" required>
          <el-input v-model="materialForm.item_name" />
        </el-form-item>
        <el-form-item label="규격">
          <el-input v-model="materialForm.specification" />
        </el-form-item>
        <el-form-item label="색상">
          <el-input v-model="materialForm.color" />
        </el-form-item>
        <el-form-item label="단위">
          <el-input v-model="materialForm.unit" style="width: 100px;" />
        </el-form-item>
        <el-form-item label="사이즈별소요량">
          <el-switch
            v-model="materialForm.is_size_dependent"
            :disabled="!sizeDistributions.length"
            active-text="ON"
            inactive-text="OFF"
          />
          <span v-if="!sizeDistributions.length" style="margin-left: 8px; color: #909399; font-size: 12px;">
            (사이즈 배분 설정 필요)
          </span>
        </el-form-item>
        <el-form-item v-if="!materialForm.is_size_dependent" label="PCS당 소요량">
          <el-input-number v-model="materialForm.usage_per_unit" :precision="4" :step="0.01" :min="0" />
        </el-form-item>
        <template v-if="materialForm.is_size_dependent">
          <el-form-item
            v-for="sd in sizeDistributions"
            :key="sd.size"
            :label="`${sd.size} 소요량`"
          >
            <el-input-number
              v-model="materialForm.size_usages[sd.size]"
              :precision="4"
              :step="0.01"
              :min="0"
              style="width: 180px;"
            />
            <span style="margin-left: 8px; color: #909399; font-size: 12px;">
              x {{ sd.quantity?.toLocaleString() }}pcs
            </span>
          </el-form-item>
        </template>
        <el-form-item label="로스율(%)">
          <el-input-number v-model="materialForm.loss_rate" :precision="2" :step="1" :min="0" />
        </el-form-item>
        <el-form-item label="통화">
          <el-select v-model="materialForm.currency" style="width: 100px;">
            <el-option label="KRW" value="KRW" />
            <el-option label="USD" value="USD" />
            <el-option label="CNY" value="CNY" />
          </el-select>
        </el-form-item>
        <el-form-item :label="materialForm.currency === 'KRW' ? '단가(원)' : '외화단가'">
          <el-input-number
            v-model="materialForm.unit_price_foreign"
            :precision="2"
            :step="materialForm.currency === 'KRW' ? 100 : 0.1"
            :min="0"
          />
        </el-form-item>
        <el-form-item label="환율" v-if="materialForm.currency !== 'KRW'">
          <el-input-number v-model="materialForm.exchange_rate" :precision="4" :step="10" :min="0" />
        </el-form-item>
        <el-form-item label="CMT포함">
          <el-switch v-model="materialForm.is_cmt" />
        </el-form-item>
        <el-form-item label="비고">
          <el-input v-model="materialForm.remarks" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showMaterialDialog = false">취소</el-button>
        <el-button type="primary" @click="submitMaterial" :loading="saving">저장</el-button>
      </template>
    </el-dialog>

    <!-- CMT 편집 다이얼로그 -->
    <el-dialog v-model="showCmtEdit" title="임가공(CMT) 정보 입력" width="500px">
      <el-form :model="cmtForm" label-width="120px">
        <el-form-item label="생산공장">
          <el-select v-model="cmtForm.manufacturer" clearable filterable placeholder="공장(협력업체) 선택"
                     @change="onCmtFactoryPicked">
            <el-option
              v-for="s in manufacturers"
              :key="s.supplier_id"
              :label="s.name"
              :value="s.supplier_id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="생산국가">
          <el-input v-model="cmtForm.country" placeholder="공장 선택 시 자동" />
        </el-form-item>
        <el-form-item label="통화">
          <el-select v-model="cmtForm.currency" style="width: 100px;">
            <el-option label="USD" value="USD" />
            <el-option label="KRW" value="KRW" />
            <el-option label="CNY" value="CNY" />
          </el-select>
        </el-form-item>
        <el-form-item label="외화 임가공비">
          <el-input-number v-model="cmtForm.cmt_price_foreign" :precision="2" :step="0.1" :min="0" />
        </el-form-item>
        <el-form-item label="환율">
          <el-input-number v-model="cmtForm.exchange_rate" :precision="4" :step="10" :min="0" />
        </el-form-item>
        <el-form-item label="비고">
          <el-input v-model="cmtForm.remarks" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showCmtEdit = false">취소</el-button>
        <el-button type="primary" @click="submitCmt" :loading="saving">저장</el-button>
      </template>
    </el-dialog>

    <!-- 경비 편집 다이얼로그 -->
    <el-dialog v-model="showExpenseEdit" title="경비(물류/관세) 정보 입력" width="500px">
      <el-form :model="expenseForm" label-width="120px">
        <el-form-item label="물류비율(%)">
          <el-input-number v-model="expenseForm.logistics_rate" :precision="2" :step="0.5" :min="0" :max="100" />
        </el-form-item>
        <el-form-item label="관세비율(%)">
          <el-input-number v-model="expenseForm.customs_rate" :precision="2" :step="0.5" :min="0" :max="100" />
        </el-form-item>
        <el-form-item label="비고">
          <el-input v-model="expenseForm.remarks" type="textarea" :rows="2" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showExpenseEdit = false">취소</el-button>
        <el-button type="primary" @click="submitExpense" :loading="saving">저장</el-button>
      </template>
    </el-dialog>

    <!-- 기본정보 편집 다이얼로그 -->
    <el-dialog v-model="showEditInfo" title="기본정보 수정" width="500px">
      <el-form :model="infoForm" label-width="120px">
        <el-alert v-if="q?.is_locked" type="info" :closable="false" show-icon
                  title="확정된 견적서 — 합의가(NEGO)와 메모만 수정할 수 있습니다" style="margin-bottom: 12px" />
        <el-form-item label="발주수량">
          <el-input-number v-model="infoForm.order_quantity" :min="1" :disabled="q?.is_locked" />
        </el-form-item>
        <el-form-item label="납기일">
          <el-date-picker v-model="infoForm.delivery_date" type="date" value-format="YYYY-MM-DD" :disabled="q?.is_locked" />
        </el-form-item>
        <el-form-item label="환율">
          <el-input-number v-model="infoForm.exchange_rate" :precision="2" :step="10" :min="0" :disabled="q?.is_locked" />
        </el-form-item>
        <el-form-item v-if="canViewCosts" label="마진율(%)">
          <el-input-number v-model="infoForm.margin_rate" :precision="2" :step="1" :min="0" :max="100" :disabled="q?.is_locked" />
        </el-form-item>
        <el-form-item v-if="canViewCosts" label="합의가(NEGO)">
          <el-input-number v-model="infoForm.negotiated_price" :precision="0" :step="1000" :min="0" style="width: 200px;" />
          <span style="margin-left: 8px; color: #909399; font-size: 12px;">원</span>
        </el-form-item>
        <el-form-item label="메모">
          <el-input v-model="infoForm.memo" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showEditInfo = false">취소</el-button>
        <el-button type="primary" @click="submitInfoEdit" :loading="saving">저장</el-button>
      </template>
    </el-dialog>

    <!-- 과거 견적 비교 다이얼로그 -->
    <CompareDialog
      v-model="showCompareDialog"
      :current-quotation="q"
    />

    <!-- 사이즈 배분 편집 다이얼로그 -->
    <el-dialog v-model="showSizeDistDialog" title="사이즈 배분 설정" width="500px">
      <div style="margin-bottom: 10px;">
        <el-button size="small" @click="addSizeRow">사이즈 추가</el-button>
      </div>
      <el-table :data="sizeDistForm" border size="small">
        <el-table-column label="사이즈" width="120">
          <template #default="{ row }">
            <el-input v-model="row.size" size="small" placeholder="예: S" />
          </template>
        </el-table-column>
        <el-table-column label="비율" width="120">
          <template #default="{ row }">
            <el-input-number v-model="row.ratio" size="small" :min="0" :precision="2" :step="1" controls-position="right" />
          </template>
        </el-table-column>
        <el-table-column label="" width="60">
          <template #default="{ $index }">
            <el-button size="small" type="danger" :icon="Delete" circle @click="sizeDistForm.splice($index, 1)" />
          </template>
        </el-table-column>
      </el-table>
      <template #footer>
        <el-button @click="showSizeDistDialog = false">취소</el-button>
        <el-button type="primary" @click="submitSizeDistributions" :loading="saving">저장</el-button>
      </template>
    </el-dialog>

    <!-- 샘플 선택 다이얼로그 -->
    <el-dialog v-model="showSampleSelectDialog" title="샘플에서 자재 가져오기" width="600px">
      <el-table :data="sampleRequests" border stripe size="small" @row-click="handleSampleSelect">
        <el-table-column prop="request_number" label="샘플요청번호" width="150" />
        <el-table-column prop="design_style_number" label="스타일번호" width="150" />
        <el-table-column prop="status_display" label="상태" width="100" />
        <el-table-column prop="request_date" label="요청일" width="120" />
      </el-table>
      <el-empty v-if="!sampleRequests.length" description="해당 디자인의 샘플요청이 없습니다." />
    </el-dialog>

    <!-- 원가 시뮬레이션 다이얼로그 (2026-07-17) — 견적가 대비 what-if.
         산식은 서버 QuotationCostCalculator와 동일 (경비 기준액=자체자재+임가공). -->
    <el-dialog v-model="showSimDialog" title="원가 시뮬레이션 — 견적가 대비 what-if" width="860px" top="4vh">
      <div class="sim-presets">
        <span class="sim-presets-label">시나리오:</span>
        <el-button size="small" @click="applyPreset('reset')">견적 그대로</el-button>
        <el-button size="small" @click="applyPreset('fx5')">환율 +5%</el-button>
        <el-button size="small" @click="applyPreset('fabric10')">원단가 +10%</el-button>
        <el-button size="small" @click="applyPreset('worst')">복합 악재</el-button>
        <el-button size="small" @click="applyPreset('nego3')">단가 인하 -3%</el-button>
      </div>

      <el-row :gutter="16" class="sim-inputs">
        <el-col :span="8">
          <div class="sim-group-title">원가 변동 가정</div>
          <div class="sim-input-row">
            <span>자체 원자재비</span>
            <el-input-number v-model="sim.rawPct" :step="1" :precision="1" size="small" controls-position="right" />
            <span class="sim-unit">%</span>
          </div>
          <div class="sim-input-row">
            <span>자체 부자재비</span>
            <el-input-number v-model="sim.accPct" :step="1" :precision="1" size="small" controls-position="right" />
            <span class="sim-unit">%</span>
          </div>
          <div class="sim-input-row">
            <span>임가공비</span>
            <el-input-number v-model="sim.cmtPct" :step="1" :precision="1" size="small" controls-position="right" />
            <span class="sim-unit">%</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="sim-group-title">경비율 / 마진율</div>
          <div class="sim-input-row">
            <span>물류비율</span>
            <el-input-number v-model="sim.logisticsRate" :step="0.5" :precision="2" :min="0" size="small" controls-position="right" />
            <span class="sim-unit">%</span>
          </div>
          <div class="sim-input-row">
            <span>관세비율</span>
            <el-input-number v-model="sim.customsRate" :step="0.5" :precision="2" :min="0" size="small" controls-position="right" />
            <span class="sim-unit">%</span>
          </div>
          <div class="sim-input-row">
            <span>마진율</span>
            <el-input-number v-model="sim.marginRate" :step="1" :precision="1" size="small" controls-position="right" />
            <span class="sim-unit">%</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="sim-group-title">판매 조건 (견적가 대비)</div>
          <div class="sim-input-row">
            <span>판매 단가(PCS)</span>
            <el-input-number v-model="sim.salePrice" :step="100" :min="0" size="small" controls-position="right" style="width: 140px" />
            <span class="sim-unit">원</span>
          </div>
          <div class="sim-input-row">
            <span>발주수량</span>
            <el-input-number v-model="sim.qty" :step="100" :min="1" size="small" controls-position="right" style="width: 140px" />
            <span class="sim-unit">PCS</span>
          </div>
          <div class="sim-hint">기본값 = 합의가(NEGO) &gt; PCS단가 순</div>
        </el-col>
      </el-row>

      <el-table :data="simRows" size="small" border class="sim-table">
        <el-table-column prop="label" label="항목" min-width="130" />
        <el-table-column label="견적 (현재)" align="right" width="140">
          <template #default="{ row }">{{ fmtWon(row.base) }}</template>
        </el-table-column>
        <el-table-column label="시뮬레이션" align="right" width="140">
          <template #default="{ row }">
            <b :class="{ 'sim-up': row.simVal > row.base, 'sim-down': row.simVal < row.base }">{{ fmtWon(row.simVal) }}</b>
          </template>
        </el-table-column>
        <el-table-column label="차이" align="right" width="140">
          <template #default="{ row }">
            <span :class="{ 'sim-up': row.simVal > row.base, 'sim-down': row.simVal < row.base }">
              {{ row.simVal === row.base ? '-' : fmtDiff(row.simVal - row.base) }}
            </span>
          </template>
        </el-table-column>
      </el-table>

      <div class="sim-verdict" :class="simResult.actualMargin >= 0 ? 'ok' : 'bad'">
        <div class="sim-verdict-row">
          <span>매출 (판매단가 × 수량)</span><b>{{ fmtWon(simResult.revenue) }}</b>
        </div>
        <div class="sim-verdict-row">
          <span>실현 마진 (매출 − 시뮬 제조원가)</span>
          <b>{{ fmtWon(simResult.actualMargin) }} ({{ simResult.actualMarginRate }}%)</b>
        </div>
        <div class="sim-verdict-row">
          <span>손익분기 판매단가 (제조원가 ÷ 수량)</span><b>{{ fmtWon(simResult.breakEvenPrice) }}</b>
        </div>
        <div class="sim-verdict-row sub">
          <span>견적 시점 계획 마진</span>
          <span>{{ fmtWon(simResult.plannedMargin) }} ({{ q?.margin_rate || 0 }}%) — 마진 잠식 {{ fmtWon(simResult.marginErosion) }}</span>
        </div>
      </div>

      <div class="sim-note">
        ※ 서버 견적 산식과 동일: 물류비·관세 = (자체자재 + 임가공) × 비율, 제조원가 = 자체자재 + 임가공 + 물류 + 관세 (브랜드 제공 자재 제외).
        시뮬레이션은 화면 계산 전용 — 견적서 데이터는 변경되지 않습니다.
      </div>
      <template #footer>
        <el-button @click="showSimDialog = false">닫기</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowLeft, ArrowDown, Lock, Refresh, Printer, DataAnalysis, Delete, DocumentCopy, Plus, TrendCharts } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axiosInstance from '@/api/axios'
import { useEstimateStore } from '@/stores/estimateStore'
import HelpPanel from '@/components/help/HelpPanel.vue'
const helpVisible = ref(false)
import { useUserStore } from '@/stores/user'
import CompareDialog from '@/components/estimate/CompareDialog.vue'

const route = useRoute()
const router = useRouter()
const store = useEstimateStore()
const userStore = useUserStore()

// 원가/마진 필드 조회 권한
const canViewCosts = computed(() => userStore.hasQuotationAccess || userStore.hasCostAccess)

const q = computed(() => store.quotation)
const activeTab = ref('info')
const showCompareDialog = ref(false)
const showMaterialDialog = ref(false)
const showCmtEdit = ref(false)
const showExpenseEdit = ref(false)
const showEditInfo = ref(false)
const showSizeDistDialog = ref(false)
const showSampleSelectDialog = ref(false)
const editingMaterial = ref(null)
const saving = ref(false)
const manufacturers = ref([])
const designMarkers = ref([])
const sampleRequests = ref([])
const sizeDistForm = ref([])

// 디자인 이미지 유무에 따른 col span 조정
const colSpan = computed(() => q.value?.design_image_url ? 4 : 4)

// 원자재/부자재 분리
const rawMaterials = computed(() => (q.value?.materials || []).filter(m => m.material_type === 'RAW'))
const accMaterials = computed(() => (q.value?.materials || []).filter(m => m.material_type === 'ACC'))

// 사이즈 배분
const sizeDistributions = computed(() => q.value?.size_distributions || [])

// 원가 요약 데이터
const costSummary = computed(() => {
  if (!q.value || !canViewCosts.value) return []
  return [
    { label: '자체 원자재비', amount: q.value.self_raw_material_cost },
    { label: '브랜드 원자재비', amount: q.value.brand_raw_material_cost },
    { label: '자체 부자재비', amount: q.value.self_accessory_cost },
    { label: '브랜드 부자재비', amount: q.value.brand_accessory_cost },
    { label: '자체부담 소계', amount: q.value.self_material_subtotal, highlight: true },
    { label: '임가공비', amount: q.value.cmt_cost },
    { label: '물류비', amount: q.value.logistics_cost },
    { label: '관세', amount: q.value.customs_cost },
    { label: '제조원가', amount: q.value.manufacturing_cost, highlight: true },
    { label: `마진 (${q.value.margin_rate}%)`, amount: q.value.margin_amount },
    { label: '견적가', amount: q.value.quotation_price, highlight: true },
    { label: 'PCS단가', amount: q.value.effective_unit_price, highlight: true },
    ...(q.value.negotiated_price ? [{ label: '합의가(NEGO)', amount: q.value.negotiated_price, highlight: true, nego: true }] : []),
  ]
})

// ═══ 원가 시뮬레이션 (2026-07-17) — 견적가 대비 what-if, 화면 계산 전용 ═══
// 산식 근거: estimate/services/cost_calculator.py (QuotationCostCalculator)
//   경비 기준액 = 자체원자재 + 자체부자재 + 임가공
//   물류비 = 기준액 × 물류비율, 관세 = 기준액 × 관세비율
//   제조원가 = 자체자재 + 임가공 + 물류 + 관세 (브랜드 제공분 제외)
const showSimDialog = ref(false)
const sim = reactive({
  rawPct: 0, accPct: 0, cmtPct: 0,
  logisticsRate: 0, customsRate: 0, marginRate: 0,
  salePrice: 0, qty: 0,
})

const num = (v) => Number(v) || 0

// 견적의 물류/관세 비율 역산 (expense_detail 없이도 동작)
const baseRates = computed(() => {
  const base = num(q.value?.self_raw_material_cost) + num(q.value?.self_accessory_cost) + num(q.value?.cmt_cost)
  return {
    base,
    logistics: base > 0 ? (num(q.value?.logistics_cost) / base) * 100 : 0,
    customs: base > 0 ? (num(q.value?.customs_cost) / base) * 100 : 0,
  }
})

function openSimulation() {
  applyPreset('reset')
  showSimDialog.value = true
}

function applyPreset(name) {
  sim.rawPct = 0; sim.accPct = 0; sim.cmtPct = 0
  sim.logisticsRate = Math.round(baseRates.value.logistics * 100) / 100
  sim.customsRate = Math.round(baseRates.value.customs * 100) / 100
  sim.marginRate = num(q.value?.margin_rate)
  sim.qty = num(q.value?.order_quantity) || 1
  sim.salePrice = Math.round(num(q.value?.negotiated_price) || num(q.value?.effective_unit_price))
  if (name === 'fx5') { sim.rawPct = 5; sim.accPct = 5; sim.cmtPct = 5 }          // 외화 결제분 환율 +5%
  else if (name === 'fabric10') { sim.rawPct = 10 }                                // 원단 단가 인상
  else if (name === 'worst') { sim.rawPct = 10; sim.cmtPct = 5; sim.logisticsRate += 1 }
  else if (name === 'nego3') { sim.salePrice = Math.round(sim.salePrice * 0.97) } // 브랜드 단가 인하 요구
}

const simCalc = computed(() => {
  const raw0 = num(q.value?.self_raw_material_cost)
  const acc0 = num(q.value?.self_accessory_cost)
  const cmt0 = num(q.value?.cmt_cost)
  const raw = raw0 * (1 + sim.rawPct / 100)
  const acc = acc0 * (1 + sim.accPct / 100)
  const cmt = cmt0 * (1 + sim.cmtPct / 100)
  const base = raw + acc + cmt
  const logistics = base * (sim.logisticsRate / 100)
  const customs = base * (sim.customsRate / 100)
  const manufacturing = base + logistics + customs
  const margin = manufacturing * (sim.marginRate / 100)
  const price = manufacturing + margin
  const qty = sim.qty || 1
  return {
    raw, acc, cmt, logistics, customs, manufacturing, margin, price,
    unitPrice: price / qty, unitCost: manufacturing / qty,
  }
})

const simRows = computed(() => {
  if (!q.value) return []
  const c = simCalc.value
  return [
    { label: '자체 원자재비', base: num(q.value.self_raw_material_cost), simVal: c.raw },
    { label: '자체 부자재비', base: num(q.value.self_accessory_cost), simVal: c.acc },
    { label: '임가공비', base: num(q.value.cmt_cost), simVal: c.cmt },
    { label: '물류비', base: num(q.value.logistics_cost), simVal: c.logistics },
    { label: '관세', base: num(q.value.customs_cost), simVal: c.customs },
    { label: '제조원가', base: num(q.value.manufacturing_cost), simVal: c.manufacturing },
    { label: 'PCS당 원가', base: num(q.value.manufacturing_cost) / (num(q.value.order_quantity) || 1), simVal: c.unitCost },
    { label: `마진 (마진율 적용)`, base: num(q.value.margin_amount), simVal: c.margin },
    { label: '견적가 (참고)', base: num(q.value.quotation_price), simVal: c.price },
  ]
})

const simResult = computed(() => {
  const c = simCalc.value
  const revenue = sim.salePrice * (sim.qty || 0)
  const actualMargin = revenue - c.manufacturing
  const plannedMargin = num(q.value?.margin_amount)
  return {
    revenue,
    actualMargin,
    actualMarginRate: revenue > 0 ? ((actualMargin / revenue) * 100).toFixed(1) : '0.0',
    breakEvenPrice: Math.ceil(c.unitCost),
    plannedMargin,
    marginErosion: plannedMargin - actualMargin,
  }
})

const fmtWon = (v) => `₩${Math.round(num(v)).toLocaleString()}`
const fmtDiff = (v) => `${v > 0 ? '+' : '−'}₩${Math.abs(Math.round(num(v))).toLocaleString()}`

// 자재 폼
const materialForm = reactive({
  quotation: null,
  material_type: 'RAW',
  item_name: '',
  specification: '',
  color: '',
  unit: 'YD',
  usage_per_unit: 0,
  loss_rate: 0,
  currency: 'KRW',
  unit_price_foreign: 0,
  exchange_rate: 1,
  unit_price_krw: 0,
  is_cmt: false,
  is_size_dependent: false,
  size_usages: {},  // {size: usage_per_unit}
  remarks: ''
})

// CMT 폼
const cmtForm = reactive({
  quotation: null,
  manufacturer: null,
  country: '',
  currency: 'USD',
  cmt_price_foreign: 0,
  exchange_rate: 1380,
  remarks: ''
})

// 경비 폼
const expenseForm = reactive({
  quotation: null,
  logistics_rate: 0,
  customs_rate: 0,
  remarks: ''
})

// 기본정보 수정 폼
const infoForm = reactive({
  order_quantity: 0,
  delivery_date: null,
  exchange_rate: 1380,
  margin_rate: 10,
  negotiated_price: null,
  memo: ''
})

const getStatusType = (status) => {
  const types = {
    DRAFT: 'info', MATERIAL_DONE: '', CMT_DONE: '', EXPENSE_DONE: '',
    COMPLETED: 'success', CONFIRMED: 'warning', SUPERSEDED: 'info', CANCELLED: 'danger'
  }
  return types[status] || 'info'
}

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-'
  return Number(amount).toLocaleString() + '원'
}

const fetchData = async () => {
  await store.fetchQuotation(route.params.id)
}

const goBack = () => router.push('/quotations')

const handlePrint = () => {
  window.open(`/quotations/${route.params.id}/print`, '_blank')
}

// 원가 재계산
const handleRecalculate = async () => {
  try {
    await store.recalculate(route.params.id)
    ElMessage.success('원가가 재계산되었습니다.')
  } catch (error) {
    console.error('Recalculate failed:', error)
  }
}

// 확정
const handleConfirm = async () => {
  try {
    await ElMessageBox.confirm('견적서를 확정(잠금)하시겠습니까?', '견적 확정')
    await store.confirm(route.params.id)
    ElMessage.success('견적서가 확정되었습니다.')
  } catch (error) {
    if (error !== 'cancel') console.error('Confirm failed:', error)
  }
}

// 확정 취소
const handleUnconfirm = async () => {
  try {
    await ElMessageBox.confirm('확정을 취소하시겠습니까?', '확정 취소')
    await store.unconfirm(route.params.id)
    ElMessage.success('확정이 취소되었습니다.')
  } catch (error) {
    if (error !== 'cancel') console.error('Unconfirm failed:', error)
  }
}

// 견적서 전환
const handleConvert = async () => {
  try {
    await ElMessageBox.confirm('사전원가를 견적서로 전환하시겠습니까?', '견적서 전환')
    const result = await store.convertToQuotation(route.params.id)
    ElMessage.success('견적서로 전환되었습니다.')
    router.push(`/quotations/${result.id}`)
  } catch (error) {
    if (error !== 'cancel') console.error('Convert failed:', error)
  }
}

const handleCreateRevision = async () => {
  try {
    await ElMessageBox.confirm(
      '현재 견적서를 기반으로 리비전을 생성합니다.\n이전 버전은 자동으로 "대체됨" 상태가 됩니다.',
      '리비전 생성'
    )
    const res = await axiosInstance.post(`v2/estimate/quotations/${route.params.id}/create_revision/`)
    ElMessage.success('리비전이 생성되었습니다.')
    router.push(`/quotations/${res.data.id}`)
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Create revision failed:', error)
      ElMessage.error('리비전 생성에 실패했습니다.')
    }
  }
}

const handleCreateMainRequest = async () => {
  // 고객사 스타일번호 사전 확인 — 메인요청부터 생산·납품 추적에 필수
  const qd = q.value
  if (qd && !(qd.design_style_number || '').trim()) {
    try {
      await ElMessageBox.confirm(
        '고객사 스타일번호가 입력되지 않았습니다.\n' +
        '메인오더(메인요청)부터는 고객사 관리번호가 필요합니다.\n' +
        '디자인 화면에서 고객사 스타일번호를 먼저 입력하세요.',
        '고객사 스타일번호 필요',
        { confirmButtonText: '디자인 열기', cancelButtonText: '닫기', type: 'warning' }
      )
      if (qd.design) router.push(`/design/${qd.design}`)
    } catch { /* 닫기 */ }
    return
  }
  try {
    const { value } = await ElMessageBox.prompt(
      '이 견적서를 기반으로 메인오더를 생성합니다.\n' +
      '생산수량에 적용할 여유율(%)을 입력하세요.\n' +
      '(예: 5 입력 → 발주 1,000 + 여유 50 = 생산 1,050)',
      '메인오더 생성',
      {
        inputValue: '5',
        inputPattern: /^\d+(\.\d{1,2})?$/,
        inputErrorMessage: '0 이상의 숫자를 입력하세요 (소수점 2자리까지)',
        confirmButtonText: '생성',
        cancelButtonText: '취소',
      }
    )
    const allowanceRate = parseFloat(value) || 0
    const res = await axiosInstance.post(
      `v2/estimate/quotations/${route.params.id}/create_main_request/`,
      { allowance_rate: allowanceRate }
    )
    ElMessage.success(res.data.message || '메인오더가 생성되었습니다.')
    // 상세 정보 새로고침 (main_requests_info 업데이트)
    await fetchData()
    // 메인요청 상세로 이동할지 확인
    try {
      await ElMessageBox.confirm(
        `메인오더 ${res.data.request_number}이(가) 생성되었습니다.\n메인오더 화면으로 이동하시겠습니까?`,
        '이동 확인'
      )
      router.push(`/main-requests/${res.data.id}`)
    } catch {
      // 취소 - 현재 페이지 유지
    }
  } catch (error) {
    if (error !== 'cancel') {
      console.error('Create main request failed:', error)
      const msg = error?.response?.data?.error || '메인오더 생성에 실패했습니다.'
      ElMessage.error(msg)
    }
  }
}

const goToMainRequest = (id) => {
  router.push(`/main-requests/${id}`)
}

const STATUS_LABELS = {
  DRAFT: '작성중',
  MATERIAL_DONE: '원부자재완료',
  CMT_DONE: '임가공완료',
  EXPENSE_DONE: '경비완료',
  COMPLETED: '완료',
  CONFIRMED: '확정',
  SUPERSEDED: '대체됨',
  CANCELLED: '취소',
}
const getStatusLabel = (status) => STATUS_LABELS[status] || status

// 자재 다이얼로그
const openMaterialDialog = (material = null) => {
  editingMaterial.value = material
  if (material) {
    // 기존 사이즈별 소요량을 dict로 변환
    const sizeUsagesMap = {}
    if (material.size_usages) {
      material.size_usages.forEach(su => {
        sizeUsagesMap[su.size] = Number(su.usage_per_unit) || 0
      })
    }
    // 사이즈 배분이 있는데 map에 없는 사이즈는 0으로 초기화
    sizeDistributions.value.forEach(sd => {
      if (!(sd.size in sizeUsagesMap)) sizeUsagesMap[sd.size] = 0
    })

    Object.assign(materialForm, {
      quotation: q.value.id,
      material_type: material.material_type,
      item_name: material.item_name,
      specification: material.specification || '',
      color: material.color || '',
      unit: material.unit || 'YD',
      usage_per_unit: Number(material.usage_per_unit) || 0,
      loss_rate: Number(material.loss_rate) || 0,
      currency: material.currency || 'KRW',
      unit_price_foreign: Number(material.unit_price_foreign) || 0,
      exchange_rate: Number(material.exchange_rate) || 1,
      unit_price_krw: Number(material.unit_price_krw) || 0,
      is_cmt: material.is_cmt || false,
      is_size_dependent: material.is_size_dependent || false,
      size_usages: sizeUsagesMap,
      remarks: material.remarks || ''
    })
  } else {
    // 새 자재 - 사이즈 배분에 따라 초기화
    const sizeUsagesMap = {}
    sizeDistributions.value.forEach(sd => {
      sizeUsagesMap[sd.size] = 0
    })
    Object.assign(materialForm, {
      quotation: q.value.id,
      material_type: 'RAW',
      item_name: '',
      specification: '',
      color: '',
      unit: 'YD',
      usage_per_unit: 0,
      loss_rate: 0,
      currency: 'KRW',
      unit_price_foreign: 0,
      exchange_rate: q.value?.exchange_rate || 1380,
      unit_price_krw: 0,
      is_cmt: false,
      is_size_dependent: false,
      size_usages: sizeUsagesMap,
      remarks: ''
    })
  }
  showMaterialDialog.value = true
}

const submitMaterial = async () => {
  if (!materialForm.item_name) {
    ElMessage.warning('품명을 입력하세요.')
    return
  }
  saving.value = true
  try {
    const { size_usages, ...formData } = materialForm
    let savedMaterial
    if (editingMaterial.value) {
      savedMaterial = await store.updateMaterial(editingMaterial.value.id, formData)
    } else {
      savedMaterial = await store.addMaterial(formData)
    }

    // 사이즈별 소요량 저장
    if (materialForm.is_size_dependent && Object.keys(size_usages).length) {
      const usages = Object.entries(size_usages)
        .filter(([, val]) => val > 0)
        .map(([size, usage_per_unit]) => ({ size, usage_per_unit }))
      if (usages.length) {
        await store.saveMaterialSizeUsages(savedMaterial.id, usages)
      }
    } else if (!materialForm.is_size_dependent && editingMaterial.value?.is_size_dependent) {
      // 사이즈별→단일로 변경 시 기존 사이즈 소요량 초기화
      await store.saveMaterialSizeUsages(editingMaterial.value.id, [])
    }

    ElMessage.success('저장되었습니다.')
    showMaterialDialog.value = false
    await fetchData()
  } catch (error) {
    console.error('Save material failed:', error)
  } finally {
    saving.value = false
  }
}

const handleDeleteMaterial = async (material) => {
  try {
    await ElMessageBox.confirm('이 자재를 삭제하시겠습니까?', '삭제')
    await store.deleteMaterial(material.id)
    ElMessage.success('삭제되었습니다.')
    await fetchData()
  } catch (error) {
    if (error !== 'cancel') console.error('Delete failed:', error)
  }
}

// CMT 저장
const submitCmt = async () => {
  saving.value = true
  try {
    const data = {
      quotation: q.value.id,
      manufacturer: cmtForm.manufacturer,
      country: cmtForm.country,
      currency: cmtForm.currency,
      cmt_price_foreign: cmtForm.cmt_price_foreign,
      exchange_rate: cmtForm.exchange_rate,
      remarks: cmtForm.remarks
    }
    if (q.value.cmt_detail) {
      await store.updateCMT(q.value.cmt_detail.id, data)
    } else {
      await store.saveCMT(data)
    }
    ElMessage.success('저장되었습니다.')
    showCmtEdit.value = false
    await fetchData()
  } catch (error) {
    console.error('Save CMT failed:', error)
  } finally {
    saving.value = false
  }
}

// 경비 저장
const submitExpense = async () => {
  saving.value = true
  try {
    const data = {
      quotation: q.value.id,
      logistics_rate: expenseForm.logistics_rate,
      customs_rate: expenseForm.customs_rate,
      remarks: expenseForm.remarks
    }
    if (q.value.expense_detail) {
      await store.updateExpense(q.value.expense_detail.id, data)
    } else {
      await store.saveExpense(data)
    }
    ElMessage.success('저장되었습니다.')
    showExpenseEdit.value = false
    await fetchData()
  } catch (error) {
    console.error('Save expense failed:', error)
  } finally {
    saving.value = false
  }
}

// 기본정보 수정
const submitInfoEdit = async () => {
  saving.value = true
  try {
    const payload = q.value?.is_locked
      ? { negotiated_price: infoForm.negotiated_price, memo: infoForm.memo }
      : { ...infoForm }
    await store.updateQuotation(route.params.id, payload)
    ElMessage.success('저장되었습니다.')
    showEditInfo.value = false
    await fetchData()
  } catch (error) {
    console.error('Update info failed:', error)
  } finally {
    saving.value = false
  }
}

// CMT 편집 다이얼로그 열릴 때 현재값 채우기
watch(showCmtEdit, (val) => {
  if (val && q.value?.cmt_detail) {
    Object.assign(cmtForm, {
      quotation: q.value.id,
      manufacturer: q.value.cmt_detail.manufacturer || null,
      country: q.value.cmt_detail.country || '',
      currency: q.value.cmt_detail.currency || 'USD',
      cmt_price_foreign: Number(q.value.cmt_detail.cmt_price_foreign) || 0,
      exchange_rate: Number(q.value.cmt_detail.exchange_rate) || 1380,
      remarks: q.value.cmt_detail.remarks || ''
    })
  } else if (val) {
    // 주의: q.manufacturer는 production.Manufacturer FK, CMT의 공장은 Supplier FK —
    // pk를 그대로 승계하면 "유효하지 않은 pk" 오류. 이름으로 협력업체를 찾아 매칭 (2026-07-18).
    const sup = manufacturers.value.find(x => x.name === q.value?.manufacturer_name)
    Object.assign(cmtForm, {
      quotation: q.value?.id,
      manufacturer: sup?.supplier_id || null,
      country: sup ? (sup.country || sup.english_country || '') : '',
      currency: 'USD',
      cmt_price_foreign: 0,
      exchange_rate: Number(q.value?.exchange_rate) || 1380,
      remarks: ''
    })
  }
})

// 경비 편집 다이얼로그 열릴 때 현재값 채우기
watch(showExpenseEdit, (val) => {
  if (val && q.value?.expense_detail) {
    Object.assign(expenseForm, {
      quotation: q.value.id,
      logistics_rate: Number(q.value.expense_detail.logistics_rate) || 0,
      customs_rate: Number(q.value.expense_detail.customs_rate) || 0,
      remarks: q.value.expense_detail.remarks || ''
    })
  } else if (val) {
    Object.assign(expenseForm, {
      quotation: q.value?.id,
      logistics_rate: 0,
      customs_rate: 0,
      remarks: ''
    })
  }
})

// 기본정보 수정 다이얼로그 열릴 때 현재값 채우기
watch(showEditInfo, (val) => {
  if (val && q.value) {
    Object.assign(infoForm, {
      order_quantity: q.value.order_quantity,
      delivery_date: q.value.delivery_date,
      exchange_rate: Number(q.value.exchange_rate) || 1380,
      margin_rate: Number(q.value.margin_rate) || 10,
      negotiated_price: q.value.negotiated_price ? Number(q.value.negotiated_price) : null,
      memo: q.value.memo || ''
    })
  }
})

// 사이즈 배분 저장
const submitSizeDistributions = async () => {
  const validSizes = sizeDistForm.value.filter(s => s.size?.trim())
  if (!validSizes.length) {
    ElMessage.warning('사이즈를 1개 이상 입력하세요.')
    return
  }
  saving.value = true
  try {
    await store.saveSizeDistributions(q.value.id, validSizes)
    ElMessage.success('사이즈 배분이 저장되었습니다.')
    showSizeDistDialog.value = false
    await fetchData()
  } catch (error) {
    console.error('Save size distributions failed:', error)
  } finally {
    saving.value = false
  }
}

const addSizeRow = () => {
  sizeDistForm.value.push({ size: '', ratio: 1 })
}

// 사이즈 배분 다이얼로그 열릴 때 현재값 채우기
watch(showSizeDistDialog, (val) => {
  if (val) {
    if (sizeDistributions.value.length) {
      sizeDistForm.value = sizeDistributions.value.map(sd => ({
        size: sd.size,
        ratio: Number(sd.ratio)
      }))
    } else {
      sizeDistForm.value = [{ size: '', ratio: 1 }]
    }
  }
})

// 사이즈 배분 합계
const getSizeDistSummary = ({ columns, data }) => {
  const sums = []
  columns.forEach((column, index) => {
    if (index === 0) { sums[index] = '합계'; return }
    if (column.property === 'ratio') {
      sums[index] = data.reduce((sum, item) => sum + Number(item.ratio || 0), 0)
    } else if (column.property === 'quantity') {
      sums[index] = data.reduce((sum, item) => sum + Number(item.quantity || 0), 0).toLocaleString()
    } else {
      sums[index] = ''
    }
  })
  return sums
}

// 마카에서 가져오기
const handleImportFromMarker = async (markerId) => {
  try {
    await ElMessageBox.confirm('선택한 마카의 사이즈 비율로 배분을 설정하시겠습니까?', '마카에서 가져오기')
    await store.importFromMarker(q.value.id, markerId)
    ElMessage.success('마카에서 사이즈 배분을 가져왔습니다.')
    await fetchData()
  } catch (error) {
    if (error !== 'cancel') console.error('Import from marker failed:', error)
  }
}

// 자재 import 드롭다운 핸들러
const handleMaterialImport = async (command) => {
  if (command === 'yardage') {
    try {
      await ElMessageBox.confirm('요척에서 자재 목록을 가져오시겠습니까?', '요척에서 가져오기')
      await store.importFromYardage(q.value.id)
      ElMessage.success('요척에서 자재를 가져왔습니다.')
      await fetchData()
    } catch (error) {
      if (error !== 'cancel') console.error('Import from yardage failed:', error)
    }
  } else if (command === 'sample') {
    await fetchSampleRequests()
    showSampleSelectDialog.value = true
  }
}

// 샘플 요청 목록 조회
const fetchSampleRequests = async () => {
  if (!q.value?.design) return
  try {
    // 샘플 API는 /api/ 바로 아래 마운트 — 잘못된 v2/sample/ 접두어로 404가 나서
    // 항상 "샘플요청이 없습니다"로 보이던 버그 수정 (2026-07-18)
    const response = await axiosInstance.get('sample-requests/', {
      params: { design: q.value.design }
    })
    sampleRequests.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch sample requests:', error)
    sampleRequests.value = []
  }
}

// 샘플 선택 후 자재 가져오기
const handleSampleSelect = async (row) => {
  try {
    await ElMessageBox.confirm(
      `${row.request_number}의 자재를 가져오시겠습니까?`,
      '샘플에서 가져오기'
    )
    await store.importSampleMaterials(q.value.id, row.id)
    ElMessage.success('샘플에서 자재를 가져왔습니다.')
    showSampleSelectDialog.value = false
    await fetchData()
  } catch (error) {
    if (error !== 'cancel') console.error('Import sample materials failed:', error)
  }
}

// 디자인 마카 목록 조회
const fetchDesignMarkers = async () => {
  if (!q.value?.design) return
  try {
    const response = await axiosInstance.get(`designs/${q.value.design}/markers/`)
    designMarkers.value = response.data || []
  } catch (error) {
    console.error('Failed to fetch markers:', error)
    designMarkers.value = []
  }
}

const getMaterialSummary = ({ columns, data }) => {
  const sums = []
  columns.forEach((column, index) => {
    if (index === 0) {
      sums[index] = '합계'
      return
    }
    if (column.property === 'amount') {
      const total = data.reduce((sum, item) => sum + Number(item.amount || 0), 0)
      sums[index] = formatCurrency(total)
    } else {
      sums[index] = ''
    }
  })
  return sums
}

const onCmtFactoryPicked = (val) => {
  const s = manufacturers.value.find(x => x.supplier_id === val)
  if (s) cmtForm.country = s.country || s.english_country || ''
}

const fetchManufacturers = async () => {
  // 주의: QuotationCMT.manufacturer는 production.Manufacturer가 아니라 Supplier FK.
  // 기존에 제조공장 목록을 넣어 pk 불일치("유효하지 않은 pk")가 나던 버그 수정 (2026-07-18).
  try {
    const response = await axiosInstance.get('suppliers/', { params: { category: 1 } })
    manufacturers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch suppliers:', error)
  }
}

// 견적 데이터 로드 후 마카 목록 조회
watch(() => q.value?.design, (newDesign) => {
  if (newDesign) fetchDesignMarkers()
})

onMounted(() => {
  fetchData()
  fetchManufacturers()
})

// 같은 라우트(/quotations/:id)에서 id만 바뀌면 컴포넌트가 재사용되어
// onMounted가 다시 돌지 않음 — 사전원가↔견적서 이동 시 이전 데이터가 보이던 버그 수정 (2026-07-18)
watch(() => route.params.id, (newId, oldId) => {
  if (newId && newId !== oldId) fetchData()
})
</script>

<style scoped>
.quotation-detail {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.header-left h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summary-row {
  margin-bottom: 20px;
}

.summary-card {
  text-align: center;
  padding: 10px;
}

.summary-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 5px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.cost-highlight {
  color: #409eff;
  font-weight: 700;
}

.nego-highlight {
  color: #e6a23c;
  font-weight: 700;
}

.detail-tabs {
  margin-top: 10px;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.tab-header span {
  font-size: 16px;
  font-weight: 600;
}

.tab-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.design-image-card {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.design-image-card :deep(.el-card__body) {
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 80px;
  color: #c0c4cc;
  font-size: 12px;
}

.size-usage-detail {
  padding: 8px 20px;
}

.size-usage-label {
  color: #606266;
  font-size: 13px;
  margin-right: 10px;
}

/* ── 견적 히스토리 타임라인 ── */
.history-timeline {
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.history-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  white-space: nowrap;
}

.history-steps {
  display: flex;
  gap: 4px;
  align-items: center;
  overflow-x: auto;
}

.history-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 12px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  min-width: 90px;
  position: relative;
  transition: all 0.2s;
}

.history-step:not(:last-child)::after {
  content: '→';
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  color: #c0c4cc;
  font-size: 12px;
  z-index: 1;
}

.history-step:hover {
  border-color: #409eff;
}

.history-step.is-current {
  border-color: #409eff;
  background: #ecf5ff;
  cursor: default;
}

.history-step.is-confirmed {
  border-color: #67c23a;
  background: #f0f9eb;
}

.history-step.is-superseded {
  opacity: 0.5;
  background: #f5f5f5;
}

.step-type {
  font-size: 10px;
  color: #909399;
}

.step-number {
  font-size: 11px;
  font-weight: 600;
  color: #303133;
}

.step-version {
  font-size: 10px;
  color: #409eff;
}

.step-status {
  font-size: 10px;
  color: #606266;
}

/* ── 원가 시뮬레이션 (2026-07-17) ── */
.sim-presets { display: flex; align-items: center; gap: 6px; margin-bottom: 14px; flex-wrap: wrap; }
.sim-presets-label { font-size: 13px; color: #909399; margin-right: 4px; }
.sim-inputs { margin-bottom: 14px; }
.sim-group-title { font-size: 12px; font-weight: 700; color: #606266; margin-bottom: 8px; }
.sim-input-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; font-size: 13px; }
.sim-input-row > span:first-child { width: 90px; color: #606266; flex-shrink: 0; }
.sim-unit { color: #909399; font-size: 12px; }
.sim-hint { font-size: 11px; color: #c0c4cc; margin-top: 4px; }
.sim-table { margin-bottom: 14px; }
.sim-up { color: #f56c6c; }
.sim-down { color: #409eff; }
.sim-verdict { border-radius: 6px; padding: 10px 14px; font-size: 13px; margin-bottom: 10px; }
.sim-verdict.ok { background: #f0f9eb; border: 1px solid #e1f3d8; }
.sim-verdict.bad { background: #fef0f0; border: 1px solid #fde2e2; }
.sim-verdict-row { display: flex; justify-content: space-between; padding: 2px 0; }
.sim-verdict-row.sub { color: #909399; font-size: 12px; border-top: 1px dashed #dcdfe6; margin-top: 4px; padding-top: 6px; }
.sim-note { font-size: 11px; color: #909399; line-height: 1.5; }
</style>
